import { BasePubSubEvent } from "@aidkitorg/types/lib/pubsub";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { get_deployment, useToken } from "./API";
import { AuthContext } from "./Context";
import { stringToColor, useInterval } from "./Util";

export const PUBSUB = process.env.NODE_ENV === 'development' 
  ? "ws://localhost:7777"
  // ? "wss://annesair.goat-mamba.ts.net:7778" // use this instead for websockets in goat mamba land
  : "wss://aklxl0yqhl.execute-api.us-east-2.amazonaws.com/prod";

export function useChannel<T extends BasePubSubEvent<string, any>>({realm, name, disabled, onMessage, onOpen, onClose, onError}: {
  realm: string,
  name: string,
  disabled?: boolean,
  onMessage: (realtimeEvent: T) => void,
  onOpen?: () => void,
  onClose?: () => void,
  onError?: () => void
}) {
  const reconnectTimeout = useRef<NodeJS.Timeout | undefined>();
  const reconnectAttempt = useRef(0);
  const socketRef = useRef<WebSocket>();
  // Keep callbacks in refs so we don't recreate the entire webhook (via setup) each time a "new" callback is passed in.
  // This means components don't have to memoize and manage their rerendering logic to use this hook without causing
  // excessive creation/tear-downs of the websocket.
  const onMessageRef = useRef(onMessage);
  const onOpenRef = useRef(onOpen);
  const onCloseRef = useRef(onClose);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onMessageRef.current = onMessage;
    onOpenRef.current = onOpen;
    onCloseRef.current = onClose;
    onErrorRef.current = onError;
  }, [onMessage, onOpen, onClose, onError]);

  const [,_token] = useToken();
  const authContext = useContext(AuthContext);
  const token = (_token || authContext?.token?.() || '').replace('auth=','')
  
  // We use a ref here because there would be a dependency loop if we used a state variable/useCallback.
  const reconnect = useRef<() => void>();
      
  const setup = useCallback(() => {
        
    if (socketRef.current) {
      socketRef.current.close();
    }

    if (!name || !token) return;
    
    if (disabled) {
      socketRef.current?.close();
      return;
    }

    console.log('Creating websocket', realm, name);

    socketRef.current = new WebSocket(PUBSUB + "?" + new URLSearchParams({ realm, channel: name, token, program: get_deployment() }).toString());
    socketRef.current.onerror = (event) => {
      console.warn("Error connecting to pubsub", event);
      onErrorRef.current?.();
    }

    socketRef.current.onopen = (event) => {
      console.log("Connected to pubsub", event);
      // Reset attempt if we successfully connect
      reconnectAttempt.current = 0;
      onOpenRef.current?.();
    }

    socketRef.current.onmessage = (event) => {
      try {
        onMessageRef.current(JSON.parse(event.data));
      } catch (error) {
        console.error("Error parsing pubsub message", error, (event as any).data);
        throw new Error("Error in Realtime " + realm + ":" + name)
      }
    }

    socketRef.current.onclose = (event) => {
      console.log("Pubsub connection closed", event);

      // Don't try to re-open if we are in development and the connection was closed by the server.
      // This is to avoid infinite loops of reconnect attempts and noise in the console.
      if (process.env.NODE_ENV === 'development' && event.code <= 1001 && document.hidden) {
        return;
      }

      onCloseRef.current?.();

      // Try to re-open with exponential backoff.
      reconnect.current?.();
    }
  }, [name, realm, token, disabled]);

  const send = useCallback((event: T) => {
    const message = JSON.stringify(event satisfies T);
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current?.send(message);
      return { status: 'success' };
    } else {
      // TODO: Handle this error status
      return { status: 'websocket_not_open' };
    }
  }, [token]);

  useEffect(() => {
    reconnect.current = () => {
      // Delay is 100ms * 2^(attempt #) multiplied by a random amount between 1 and 1.2 with a max of 30 seconds.
      const delay = Math.min(100 * (2 ** (reconnectAttempt.current - 1)), 30000) * (1 + (Math.random() * 0.2));
  
      // No need to go beyond 9 as it hits our 30 second max.
      if (reconnectAttempt.current < 9) {
        reconnectAttempt.current += 1;
      }
      console.warn(`Pubsub connection closed. Will try again in ${delay / 1000} seconds`);
      
      reconnectTimeout.current = setTimeout(() => {
        // Check one more time that the socket is closed before attempting to reopen.
        if (socketRef.current?.readyState !== WebSocket.OPEN) {
          setup();
        }
      }, delay);
    }
    setup();
    return () => {
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
      socketRef.current?.close();
    }
  }, [setup]);

  return send;
}

export const inits = (str: string) => {
  if (str) return str.split('/')[0].split(' ').filter((v: string) => v).map((w: string) => w[0]).join('');

  return '';
}

export function FacePile(props: {
  name: string,
  channel: string,
  unsavedChanges?: () => boolean,
  unsavedChangesText?: string,
  browserTab?: string,
  lightness?: "90%" | "75%" }) {
  const [userList, setUserList] = useState({} as Record<string, { name: string, lastSeen: number, unsavedChanges?: boolean, browserTab?: string }>);
  const [warned, setWarned] = useState(false);

  const facePileCallback = useCallback((data: any) => {
    // console.log("Data:", data);
    if (data.event === "heartbeat") {
      setUserList((lastSeen) => ({ ...lastSeen, [data.data.name + '/' + data.data.browserTab]: { lastSeen: Date.now(), ...data.data }}));
    }

    if (data.event === "remove_user") {
      setUserList((lastSeen) => {
        const newUsers = lastSeen;
        delete newUsers[data.data.name + '/' + data.data.browserTab];
        return newUsers;
      });
    }
  }, []);

  const sendData = useChannel({
    realm: 'facepile',
    name: props.channel,
    onMessage: facePileCallback,
  });

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      sendData({
        realm: 'facepile',
        channel: props.channel,
        event: 'remove_user',
        data: {
          name: props.name,
          browserTab: props.browserTab
        }
      }); 
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [sendData, props.name, props.browserTab, props.channel]);

  useInterval(() => {
    let unsaved = props.unsavedChanges?.() || false;
    if (props.name && document.visibilityState === "visible") {
      sendData({
        realm: 'facepile',
        channel: props.channel,
        event: 'heartbeat',
        data: {
          name: props.name,
          unsavedChanges: unsaved,
          browserTab: props.browserTab
        }
      }); 
    }
    const ul = userList;
    const otherPeopleEdits = Object.keys(ul).map((n) => ul[n]).filter(u => u.unsavedChanges && u.name !== props.name).length > 0;
    const otherTabEdits = Object.keys(ul).map((n) => ul[n]).filter(u => u.unsavedChanges && u.name === props.name && u.browserTab !== props.browserTab).length > 0;
    // console.log(otherPeopleEdits, otherTabEdits)
    if (unsaved && !warned && (otherPeopleEdits || otherTabEdits)) {
      if (otherPeopleEdits) {
        alert("Warning! You're making a change when someone else has outstanding edits!");
      }
      if (otherTabEdits) {
        alert("Warning! You're making a change when you have edits in a separate tab!");
      }
      setWarned(true);
    }
    if (!otherPeopleEdits && !otherTabEdits) {
      setWarned(false);
    }
  }, 1000);
    
  return <div className="inline-block">{Object.keys(userList).map((k) => {
    let extraStyle = {}
    let extraTitle = ''
    if (userList[k].unsavedChanges) {
      extraStyle = { border: '2px solid red' };
      extraTitle = ' - ' + (props.unsavedChangesText || 'Unsaved Changes')
    }
    // When we haven't had a heartbeat from someone in 20 seconds, show them greyed out.
    if (Date.now() - userList[k].lastSeen > 20000) {
      extraStyle = { ...extraStyle, opacity: 0.5 };
    }
    return <div key={k} title={`${k.split('/')[0]} ${extraTitle} (${userList[k].browserTab})`} className={"inline-block ml-2 p-1 radius-lg text-white rounded-full cursor-pointer "} style={{backgroundColor: stringToColor(k, props.lightness || '90%'), ...extraStyle}}>{inits(k)}</div>
  })}</div>
}
